// Dependencies
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import dayjs from 'dayjs';

// State management
import { store, dispatch } from '../store/store';
import { API_CONFIG } from './config';
import { decimalSeparators } from 'src/@types/preferences.type';
import { notAuthUser } from 'src/store/slices/auth';

/**
 * Get the current locale settings
 */
const getLocales = () => {
  // Date format
  const dateFormat = dayjs.localeData().longDateFormat('L');
  // Time format
  const timeFormat = dayjs.localeData().longDateFormat('LT');
  // Time zone
  const timeZone = dayjs.tz.guess();
  // Language
  const language = store.getState().auth?.preferences?.language || 'en';
  // Get parts of the number format
  const parts = new Intl.NumberFormat().formatToParts(1234.61);
  // Decimal and thousand separators
  const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value || '.';
  const decimalSeparatorKey =
    Object.entries(decimalSeparators).find(
      ([, separator]) => separator === decimalSeparator
    )?.[0] || '';
  const thousandSeparator = parts.find((part) => part.type === 'group')?.value || '';
  const thousandSeparatorKey =
    Object.entries(decimalSeparators).find(
      ([, separator]) => separator === thousandSeparator
    )?.[0] || '';

  // Result
  return {
    dateFormat,
    timeFormat,
    timeZone,
    language,
    decimalSeparator: decimalSeparatorKey,
    thousandSeparator: thousandSeparatorKey,
  };
};

/**
 * Convert a string to base64
 */
const generateHex = (str: string) => {
  // Convert the string to a byte array
  const text = new TextEncoder().encode(str);

  // Convert the byte array to a string
  const binString = Array.from(text, (byte) => String.fromCodePoint(byte)).join('');

  // Convert the string to base64
  const base64 = Buffer.from(binString).toString('base64');

  // Convert base64 to hex
  const hex = Buffer.from(base64, 'base64').toString('hex');

  // Return the hex
  return hex;
};

/**
 * Headers interceptor
 */
export function headersInterceptor(config: InternalAxiosRequestConfig) {
  // Check headers
  if (!config.headers) return config;

  // Get token from local storage
  const token = localStorage.getItem('token');

  // Add headers
  config.headers.Authorization = token ? `Bearer ${token}` : undefined;
  config.headers['interface-info'] = generateHex(JSON.stringify(getLocales()));

  // Return the updated config
  return config;
}

/**
 * Refresh token on rejected function
 */
export function refreshTokenOnRejectedFn(api: AxiosInstance) {
  return async (error: AxiosError) => {
    // Check config
    if (!error.config) return Promise.reject(error);

    // Save the original request
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _attempts?: number;
    };

    // Check if the error is a 401 error and the original request has not been retried yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !!localStorage.getItem('token')
    ) {
      // Mark the request as retried to avoid infinite loops.
      originalRequest._retry = true;

      try {
        // Make a request to your auth server to refresh the token.
        const { data } = await axios.post('/auth/refresh', undefined, {
          baseURL: API_CONFIG.baseURL,
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        // Store the new access and refresh tokens.
        localStorage.setItem('token', data?.data.accessToken);

        // Update the authorization header with the new access token.
        api.defaults.headers.Authorization = `Bearer ${data?.data.accessToken}`;

        // Retry the original request with the new access token.
        return api(originalRequest);
      } catch (requestError) {
        // If its an axios error
        if (isAxiosError(requestError)) {
          // If the error code is UNAUTHORIZED
          if (requestError.response?.data?.error.code === 'UNAUTHORIZED') {
            // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
            localStorage.removeItem('token');

            // Change state to not authorized
            dispatch(notAuthUser());

            // Return the error
            return Promise.reject(error);
          } else if (
            requestError.response?.data?.error.code === 'CONFLICT' &&
            (originalRequest._attempts || 0) < 3
          ) {
            // Random count of ms from 500 to 2500 to wait before retry
            const random = Math.floor(Math.random() * 2000) + 500;

            // Wait for random ms before retrying
            await new Promise((resolve) => setTimeout(resolve, random));

            // Increment the attempts counter
            originalRequest._attempts = (originalRequest._attempts || 0) + 1;

            // Reset the retry flag
            originalRequest._retry = false;

            // Retry the original request
            return api(originalRequest);
          }
        }
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !localStorage.getItem('token')
    ) {
      // Change state to not authorized
      dispatch(notAuthUser());
    }
    return Promise.reject(error); // For all other errors, return the error as is.
  };
}
